import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface DocHubSnippet {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  code: string;
  language?: string;
  category_id?: string;
  attachments: string[];
  tags: string[];
  slug?: string;
  created_at: string;
  updated_at: string;
}

export const useDocHubSnippets = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: snippets,
    isLoading,
    error
  } = useQuery({
    queryKey: ['doc-hub-snippets', brandId],
    queryFn: async (): Promise<DocHubSnippet[]> => {
      const { data, error } = await supabase
        .from('os_doc_hub_snippets')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(snippet => ({
        ...snippet,
        attachments: Array.isArray(snippet.attachments) ? snippet.attachments : 
                    typeof snippet.attachments === 'string' ? JSON.parse(snippet.attachments) : [],
        tags: Array.isArray(snippet.tags) ? snippet.tags : 
              typeof snippet.tags === 'string' ? JSON.parse(snippet.tags) : [],
      }));
    },
    enabled: !!brandId,
  });

  const createSnippetMutation = useMutation({
    mutationFn: async (snippetData: Omit<DocHubSnippet, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_doc_hub_snippets')
        .insert({
          ...snippetData,
          attachments: snippetData.attachments,
          tags: snippetData.tags,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-snippets', brandId] });
      toast({
        title: "Success",
        description: "Snippet created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating snippet:', error);
      toast({
        title: "Error",
        description: "Failed to create snippet",
        variant: "destructive",
      });
    },
  });

  const updateSnippetMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DocHubSnippet> & { id: string }) => {
      const updateData: any = { ...updates };
      if (updates.attachments !== undefined) {
        updateData.attachments = updates.attachments;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }

      const { data, error } = await supabase
        .from('os_doc_hub_snippets')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-snippets', brandId] });
      toast({
        title: "Success",
        description: "Snippet updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating snippet:', error);
      toast({
        title: "Error",
        description: "Failed to update snippet",
        variant: "destructive",
      });
    },
  });

  const deleteSnippetMutation = useMutation({
    mutationFn: async (snippetId: string) => {
      const { error } = await supabase
        .from('os_doc_hub_snippets')
        .delete()
        .eq('id', snippetId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-snippets', brandId] });
      toast({
        title: "Success",
        description: "Snippet deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting snippet:', error);
      toast({
        title: "Error",
        description: "Failed to delete snippet",
        variant: "destructive",
      });
    },
  });

  const createSnippet = (snippetData: Omit<DocHubSnippet, 'id' | 'created_at' | 'updated_at'>) => {
    createSnippetMutation.mutate(snippetData);
  };

  const updateSnippet = (id: string, updates: Partial<DocHubSnippet>) => {
    updateSnippetMutation.mutate({ id, ...updates });
  };

  const deleteSnippet = (id: string) => {
    deleteSnippetMutation.mutate(id);
  };

  return {
    snippets: snippets || [],
    isLoading,
    error,
    createSnippet,
    updateSnippet,
    deleteSnippet,
    isCreating: createSnippetMutation.isPending,
    isUpdating: updateSnippetMutation.isPending,
    isDeleting: deleteSnippetMutation.isPending,
  };
};
